import { AdTypes, storeCachedAds, getCachedAds } from "@shared/services/cached_ads";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import { ListType } from "@shared/types/QuickOrderList";
import { determineListAdParams } from "@shared/services/list_ads_service";
import {
  clearQuickOrderListBanners,
  QuickOrderListBannersStore,
} from "@retailer/svelte/stores/QuickOrderListBannersStore";
import { get as getStoreValue } from "svelte/store";
import { isEqual } from "lodash";
import { stagedRolloutCheck } from "@shared/services/staged_rollouts";

import type { QuickOrderListAdParams } from "@shared/types/QuickOrderList";

export const getProductIndexBanners = async (
  pageName: string,
  pageValue: string = undefined,
  bannerParams: any = {},
) => {
  const options = {
    page_name: pageName,
    page_value: pageValue,
    page: bannerParams.page,
    ...bannerParams,
  };

  let bannersConfig = [];
  if (bannerParams.page && parseInt(bannerParams.page) > 1) {
    if (stagedRolloutCheck('kevel_ads')) {
      bannersConfig = getCachedAds(AdTypes.ProductIndexBanner, bannerParams, 2);

      return bannersConfig;
    } else {
      bannersConfig = getCachedAds(AdTypes.ProductIndexBanner, bannerParams, 1);

      if (bannersConfig.length > 0) {
        return bannersConfig[0];
      }
    }

    
  } else {
    const res = await HttpPromise({
      method: "GET",
      url: `/api/retailer/banners/product_index_banners`,
      data: options,
    });

    if (stagedRolloutCheck('kevel_ads')) {
      if (res.length > 0) {
        storeCachedAds(AdTypes.ProductIndexBanner, res[0][0].banners, bannerParams);
      }

      bannersConfig = getCachedAds(AdTypes.ProductIndexBanner, bannerParams, 2);

      return bannersConfig;
    } else {
      if (res.length > 0) {
        storeCachedAds(AdTypes.ProductIndexBanner, res, bannerParams);
      }

      bannersConfig = getCachedAds(AdTypes.ProductIndexBanner, bannerParams, 1);

      // Banner configs are served as an array of configuration arrays.
      // We need to extract it here so that all of the banners in the one
      // config array are fed into the page components correctly.

      return bannersConfig[0];
    }
  }
};

export const getInternalBanner = async (pageName: string, pageValue: string | null = null) => {
  const options = {
    method: "GET",
    url: "/api/retailer/banners/internal_banners",
    data: {
      page_name: pageName,
      page_value: pageValue,
    },
  };

  return await HttpPromise(options);
};

export const getExternalBanner = async (
  pageName: string,
  pageValue: string | null,
  filterData: any,
) => {
  const options = {
    method: "GET",
    url: "/api/retailer/banners/external_banners",
    data: {
      page_name: pageName,
      page_value: pageValue,
      ...filterData,
    },
  };

  return await HttpPromise(options);
};

export const getListBanners = async (
  listType: ListType,
  filters: any,
  stateParams: any,
  productCount: number,
) => {
  const newListId =
    listType === ListType.MySavedProducts ? ListType.MySavedProducts : stateParams.listId;
  const currentStoreValue = getStoreValue(QuickOrderListBannersStore);

  const storedListId = currentStoreValue.listId;
  const storedBannerFilterData: QuickOrderListAdParams | null = currentStoreValue.bannerFilterData;
  const storedPage = currentStoreValue.page;

  let newBanners = [];
  let showNewBanners = false;
  let newListBannerParams: QuickOrderListAdParams | null = null;

  if ((productCount || 0) === 0) {
    clearQuickOrderListBanners();
  } else {
    newListBannerParams = determineListAdParams(stateParams, filters);

    // If filters are applied that we don't care about (e.g. container), don't refetch
    const hasPaginated = stateParams.page && storedPage !== stateParams.page;
    if (
      !!newListBannerParams &&
      (storedListId !== newListId ||
        !isEqual(storedBannerFilterData, newListBannerParams) ||
        hasPaginated)
    ) {
      showNewBanners = true;
      newBanners = await getExternalBanner("lists", null, newListBannerParams);

      // If on the same list and the new banners are empty, keep the current banners
      if (storedListId === newListId && (newBanners || []).length === 0) {
        showNewBanners = false;
      }
    }
  }

  if (showNewBanners) {
    clearQuickOrderListBanners();
    QuickOrderListBannersStore.set({
      banners: newBanners,
      listId: newListId,
      bannerFilterData: newListBannerParams,
      page: stateParams.page,
    });
  }
};
